import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { EQUIPMENT_TYPES } from '../../../data/nurseAppModels';
import EquipmentAssignmentModal from './EquipmentAssignmentModal';

const EquipmentPlanManagementPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { equipment, catalogs, getEquipmentAssignments, assignEquipmentToShift, removeEquipmentAssignment } = useEquipment();
  const { plans } = useShiftPlan();
  const { currentTeam } = useTeam();
  
  const [plan, setPlan] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Fetch plan and shifts
  useEffect(() => {
    const fetchPlanData = async () => {
      if (!planId || !currentTeam) return;

      try {
        setLoading(true);

        // Find plan
        const planData = plans.find(p => p.id === planId);
        if (!planData) {
          console.error('Plan not found');
          return;
        }

        setPlan(planData);

        // Fetch shifts for this plan
        const shiftsQuery = query(
          collection(db, 'shifts'),
          where('teamId', '==', currentTeam.id),
          where('planId', '==', planId)
        );
        const shiftsSnapshot = await getDocs(shiftsQuery);
        const fetchedShifts = shiftsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setShifts(fetchedShifts);

        // Fetch equipment assignments
        const fetchedAssignments = await getEquipmentAssignments(planId, planData.version);
        setAssignments(fetchedAssignments);

      } catch (err) {
        console.error('Error fetching plan data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlanData();
  }, [planId, currentTeam, plans, getEquipmentAssignments]);

  // Handle date click (for calendar view)
  const handleDateClick = (date) => {
    setSelectedDate(date);
    // Find shifts for this date
    const dateShifts = shifts.filter(shift => {
      const shiftDate = shift.startTime?.toDate ? shift.startTime.toDate() : new Date(shift.startTime?.seconds * 1000);
      return shiftDate.toDateString() === date.toDateString();
    });
    
    if (dateShifts.length > 0) {
      setSelectedShift(dateShifts[0]); // Select first shift for now
      setShowAssignmentModal(true);
    }
  };

  // Handle assign equipment
  const handleAssignEquipment = async (equipmentId, assignmentData) => {
    if (!selectedShift || !plan) return;

    try {
      const result = await assignEquipmentToShift(
        plan.id,
        plan.version,
        selectedShift.id,
        equipmentId,
        assignmentData
      );

      if (result.success) {
        // Refresh assignments
        const fetchedAssignments = await getEquipmentAssignments(plan.id, plan.version);
        setAssignments(fetchedAssignments);
        setShowAssignmentModal(false);
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error assigning equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  // Handle remove assignment
  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('ยืนยันการลบการกำหนดอุปกรณ์?')) return;

    try {
      const result = await removeEquipmentAssignment(assignmentId);
      if (result.success) {
        // Refresh assignments
        const fetchedAssignments = await getEquipmentAssignments(plan.id, plan.version);
        setAssignments(fetchedAssignments);
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  // Get assignments for a shift
  const getShiftAssignments = (shiftId) => {
    return assignments.filter(a => a.shiftId === shiftId);
  };

  // Calculate summary
  const summary = {
    totalShifts: shifts.length,
    shiftsWithEquipment: new Set(assignments.map(a => a.shiftId)).size,
    shiftsWithoutEquipment: shifts.length - new Set(assignments.map(a => a.shiftId)).size,
    totalAssignments: assignments.length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">กำลังโหลด...</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">ไม่พบแผนเวร</p>
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับไปหน้ารายการแผน
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts/plans')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            จัดการอุปกรณ์ - {plan.planNumber}
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            <p>✅ Version {plan.version} (ACTIVE)</p>
            <p>เดือน: {plan.targetMonth}</p>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">📊 สรุปอุปกรณ์</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">เวรทั้งหมด</p>
              <p className="text-2xl font-bold text-blue-700">{summary.totalShifts}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">กำหนดอุปกรณ์แล้ว</p>
              <p className="text-2xl font-bold text-green-700">{summary.shiftsWithEquipment}</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">ยังไม่ได้กำหนด</p>
              <p className="text-2xl font-bold text-yellow-700">{summary.shiftsWithoutEquipment}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">การกำหนดทั้งหมด</p>
              <p className="text-2xl font-bold text-purple-700">{summary.totalAssignments}</p>
            </div>
          </div>
        </div>

        {/* Equipment Assignments List */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">📋 รายการอุปกรณ์ที่กำหนดแล้ว</h2>
            <button
              onClick={() => {
                if (shifts.length > 0) {
                  setSelectedShift(shifts[0]);
                  setShowAssignmentModal(true);
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              + เพิ่มอุปกรณ์
            </button>
          </div>

          {assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>ยังไม่มีอุปกรณ์ที่กำหนด</p>
              <p className="text-sm mt-2">คลิกปุ่ม "+ เพิ่มอุปกรณ์" เพื่อเริ่มกำหนดอุปกรณ์</p>
            </div>
          ) : (
            <div className="space-y-4">
              {shifts
                .filter(shift => getShiftAssignments(shift.id).length > 0)
                .map(shift => {
                  const shiftAssignments = getShiftAssignments(shift.id);
                  const shiftDate = shift.startTime?.toDate 
                    ? shift.startTime.toDate() 
                    : new Date(shift.startTime?.seconds * 1000);

                  return (
                    <div key={shift.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-gray-800">
                            {shiftDate.toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                          <p className="text-sm text-gray-600">
                            เวร{shift.shiftType === 'MORNING' ? 'เช้า' : shift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'} - {shift.assignedToName}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedShift(shift);
                            setShowAssignmentModal(true);
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          เพิ่มอุปกรณ์
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {shiftAssignments.map(assignment => (
                          <div key={assignment.id} className="bg-gray-50 rounded p-3 flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-800">
                                {assignment.equipmentName} ({assignment.equipmentCode})
                              </p>
                              {assignment.equipmentType === EQUIPMENT_TYPES.SMALL_ITEM && (
                                <p className="text-sm text-gray-600">
                                  จำนวน: {assignment.quantity}
                                </p>
                              )}
                              {assignment.notes && (
                                <p className="text-xs text-gray-500 mt-1">หมายเหตุ: {assignment.notes}</p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveAssignment(assignment.id)}
                              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                            >
                              ลบ
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Equipment Assignment Modal */}
      {showAssignmentModal && selectedShift && (
        <EquipmentAssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedShift(null);
          }}
          shift={selectedShift}
          planId={plan.id}
          planVersion={plan.version}
          existingAssignments={getShiftAssignments(selectedShift.id)}
          onAssign={handleAssignEquipment}
        />
      )}
    </div>
  );
};

export default EquipmentPlanManagementPage;


